import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { LogOut, User, Lock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, formatCountdown } from "@/lib/api";

const links = [
  { to: "/hjem", label: "Hjem" },
  { to: "/forudsigelser", label: "Forudsigelser" },
  { to: "/leaderboard", label: "Leaderboard" },
  { to: "/leagues", label: "Ligaer" },
  { to: "/statistik", label: "Statistik", lockable: true },
  { to: "/nyheder", label: "Nyheder" },
  { to: "/regler", label: "Regler" },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { data: festData } = useQuery({ queryKey: ["festivals"], queryFn: () => api.get("/festivals").then(r => r.data), enabled: !!user });
  const statsLocked = festData?.deadline ? !formatCountdown(festData.deadline).locked : true;

  return (
    <header className="fixed top-0 inset-x-0 z-40 glass" data-testid="main-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/hjem" className="flex items-baseline gap-2" data-testid="nav-logo">
          <span className="font-score text-2xl leading-none">{festData?.season?.short_name || "FM27"}</span>
          <span className="hidden sm:inline label-eyebrow">Festivalmanager</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {links.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace('/', '')}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-lg flex items-center gap-1.5 ${isActive ? "text-white bg-white/10" : "text-zinc-400 hover:text-white hover:bg-white/5"}`
              }
            >
              {l.lockable && statsLocked && <Lock size={11} className="opacity-70" />}
              {l.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              data-testid="nav-link-admin"
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-lg font-semibold ${isActive ? "text-black bg-orange-500" : "text-orange-400 hover:bg-orange-500/10"}`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>
        <div className="flex items-center gap-2">
          {user && (
            <>
              <Link to="/profil" className="hidden sm:flex items-center gap-2 text-sm text-zinc-300 hover:text-white" data-testid="nav-profile-link">
                {(user.effective_picture || user.picture) ? (
                  <img src={user.effective_picture || user.picture} alt="" className="w-7 h-7 rounded-full border border-white/20 object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center"><User size={14} /></div>
                )}
                <span data-testid="nav-user-name" className="max-w-[120px] truncate">{user.display_name || user.name}</span>
              </Link>
              <Button size="sm" variant="ghost" onClick={logout} data-testid="nav-logout-btn" className="text-zinc-300 hover:text-white">
                <LogOut size={16} />
              </Button>
            </>
          )}
        </div>
      </div>
      {/* Mobile bottom nav */}
      <div className="md:hidden flex justify-around border-t border-white/10 py-2 overflow-x-auto">
        {links.map(l => (
          <NavLink key={l.to} to={l.to} className={({ isActive }) => `text-xs px-2 py-1 rounded whitespace-nowrap ${isActive ? "text-white" : "text-zinc-500"}`} data-testid={`mnav-${l.to.replace('/', '')}`}>
            {l.label}
          </NavLink>
        ))}
        <NavLink to="/profil" className={({ isActive }) => `text-xs px-2 py-1 rounded whitespace-nowrap ${isActive ? "text-white" : "text-zinc-500"}`}>Profil</NavLink>
        {user?.is_admin && (
          <NavLink to="/admin" className={({ isActive }) => `text-xs px-2 py-1 rounded whitespace-nowrap ${isActive ? "text-orange-400" : "text-orange-500/70"}`}>Admin</NavLink>
        )}
      </div>
    </header>
  );
}
